import type { VoiceFlowTurn } from 'types';

// Live transcript fragments are a continuous stream, not completed turns.
// Preserve raw text and timing; application snapshots remain revisable until commit.
export interface LiveTranscriptFragment {
  eventId: string;
  delta: string;
  startMs: number | null;
  endMs: number | null;
}

export const newLiveId = (): string => {
  const bytes = new Uint8Array(16);
  globalThis.crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

// UTF-8 bytes conservatively bound BPE tokens, including non-English content.
// Never trim, split a code point, or silently truncate authored instructions.
export const splitLiveContext = (content: string): string[] => {
  const chunks: string[] = [];
  let chunk = '';
  let bytes = 0;
  for (const character of content) {
    const codePoint = character.codePointAt(0) ?? 0;
    const width = codePoint <= 0x7f ? 1 : codePoint <= 0x7ff ? 2 : codePoint <= 0xffff ? 3 : 4;
    if (bytes + width > 450) { chunks.push(chunk); chunk = ''; bytes = 0; }
    chunk += character;
    bytes += width;
  }
  if (chunk) chunks.push(chunk);
  return chunks;
};

export class LiveTranscriptCapture {
  readonly id: string;
  timestamp = '';
  revision = 0;
  text = '';
  readonly fragments: LiveTranscriptFragment[] = [];
  private readonly seen = new Set<string>();

  constructor(id = newLiveId()) { this.id = id; }

  append(event: Record<string, unknown>, sessionStartedAt?: number): boolean {
    if (typeof event.delta !== 'string' || !event.delta) return false;
    const eventId = typeof event.event_id === 'string' ? event.event_id : '';
    if (eventId && this.seen.has(eventId)) return false;
    if (eventId) this.seen.add(eventId);
    if (!this.timestamp) {
      // Native fragments can arrive after the other speaker's reply. Their
      // session-relative time, not network delivery or save time, owns the row.
      const start = event.start_ms;
      const occurredAt = typeof start === 'number' && Number.isFinite(start) && start >= 0
        && typeof sessionStartedAt === 'number' && Number.isFinite(sessionStartedAt)
        ? sessionStartedAt + start : Date.now();
      this.timestamp = new Date(Number.isFinite(occurredAt) && Math.abs(occurredAt) < 8.64e15
        ? occurredAt : Date.now()).toISOString();
    }
    this.fragments.push({
      eventId,
      delta: event.delta,
      startMs: typeof event.start_ms === 'number' ? event.start_ms : null,
      endMs: typeof event.end_ms === 'number' ? event.end_ms : null,
    });
    this.text += event.delta;
    this.revision += 1;
    return true;
  }
}

export const liveAssistantEntries = (turns: VoiceFlowTurn[], captures: LiveTranscriptCapture[]) => {
  const entries = new Map<string, { id: string; text: string; timestamp: string; source: 'history' }>();
  const covered = new Map<string, string>();
  const compact = (text: string) => text.replace(/\s+/g, '');
  const timestampFor = (turn: VoiceFlowTurn) => {
    const startedAt = turn.metadata.live_assistant_started_at;
    return typeof startedAt === 'string' && Number.isFinite(Date.parse(startedAt)) ? startedAt : turn.created_at;
  };
  const addContext = (id: string, turn: VoiceFlowTurn) => {
    const groupId = covered.get(id);
    const group = groupId ? entries.get(groupId) : undefined;
    if (group && compact(group.text).includes(compact(turn.assistant_transcript))) return;
    const saved = entries.get(id);
    // The provider capture preserves boundary whitespace; API serializers trim
    // it when persisting snapshots. Compare the same formatting boundary without
    // changing words or treating a different capture as the same utterance.
    const savedText = saved?.text.trim() ?? '';
    const contextText = turn.assistant_transcript.trim();
    if (!saved) entries.set(id, { id, text: turn.assistant_transcript, timestamp: timestampFor(turn), source: 'history' });
    else if (contextText.length > savedText.length && contextText.startsWith(savedText)) {
      entries.set(id, { ...saved, text: turn.assistant_transcript });
    } else if (!savedText.startsWith(contextText)) {
      // Conflicting snapshots are not duplicates merely because a caller linked
      // them. Keep the otherwise-unrepresented text rather than losing history.
      entries.set(String(turn.id), {
        id: String(turn.id), text: turn.assistant_transcript, timestamp: timestampFor(turn), source: 'history',
      });
    }
  };
  const canonical = turns.filter((turn) => typeof turn.metadata.live_assistant_capture_id === 'string');
  canonical.forEach((turn) => {
    const id = turn.metadata.live_assistant_capture_id as string;
    entries.set(id, { id, text: turn.assistant_transcript, timestamp: timestampFor(turn), source: 'history' });
  });
  captures.forEach((capture) => {
    if (!capture.text.trim()) return;
    const saved = entries.get(capture.id);
    const savedText = saved?.text.trim() ?? '';
    const capturedText = capture.text.trim();
    // Local captures are append-only. A delayed polling snapshot must not replace
    // newer streamed words, or move a bubble to the time it happened to be saved.
    entries.set(capture.id, {
      id: capture.id, text: saved && savedText.length > capturedText.length && savedText.startsWith(capturedText)
        ? saved.text : capture.text,
      timestamp: capture.timestamp, source: 'history',
    });
  });
  turns.forEach((turn) => {
    if (!turn.assistant_transcript.trim() || typeof turn.metadata.live_assistant_capture_id === 'string') return;
    const contextId = turn.metadata.live_assistant_context_capture_id;
    if (typeof contextId === 'string') {
      addContext(contextId, turn);
      return;
    }
    const contextIds = turn.metadata.live_assistant_context_capture_ids;
    if (Array.isArray(contextIds) && contextIds.length) {
      const ids = contextIds.map(String);
      if (ids.length === 1) { addContext(ids[0], turn); return; }
      const sources = ids.map((id) => entries.get(covered.get(id) ?? id));
      const text = compact(turn.assistant_transcript);
      if (sources.every(Boolean) && compact(sources.map((source) => source!.text).join('')).includes(text)) return;
      // A failed final snapshot can leave several prefixes, with no reliable
      // way to locate the missing boundaries inside the saved narration. Show
      // that complete passage once and cover only its explicitly linked parts.
      const id = `narration-${turn.id}`;
      entries.set(id, {
        id, text: turn.assistant_transcript,
        timestamp: sources.find(Boolean)?.timestamp ?? turn.created_at, source: 'history',
      });
      ids.forEach((sourceId, index) => {
        const source = sources[index];
        if (!source || text.includes(compact(source.text))) {
          entries.delete(covered.get(sourceId) ?? sourceId);
          covered.set(sourceId, id);
        }
      });
      return;
    }
    // Older local Live sessions did not attach source IDs to evaluation/narration
    // context. Only fold that explicitly tagged context into its preceding exact
    // snapshot; independently spoken, identical questions keep their own IDs.
    if (turn.metadata.live_capture_id || turn.metadata.playback_confirmed) {
      const preceding = canonical.filter((item) => item.id < turn.id).slice(-1)[0];
      if (preceding?.assistant_transcript === turn.assistant_transcript) return;
    }
    const id = String(turn.id);
    entries.set(id, { id, text: turn.assistant_transcript, timestamp: turn.created_at, source: 'history' });
  });
  return [...entries.values()].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
};

export const waitForLiveIceGathering = (peer: RTCPeerConnection): Promise<void> => {
  if (peer.iceGatheringState === 'complete') return Promise.resolve();
  return new Promise((resolve, reject) => {
    const cleanup = () => {
      window.clearTimeout(timeout);
      peer.removeEventListener('icegatheringstatechange', check);
      peer.removeEventListener('connectionstatechange', check);
    };
    const check = () => {
      if (peer.connectionState === 'closed') {
        cleanup();
        reject(new Error('Voice connection was closed.'));
      } else if (peer.iceGatheringState === 'complete') {
        cleanup();
        resolve();
      }
    };
    const timeout = window.setTimeout(() => {
      cleanup();
      reject(new Error('Unable to gather a complete voice connection offer. Please reconnect.'));
    }, 15000);
    peer.addEventListener('icegatheringstatechange', check);
    peer.addEventListener('connectionstatechange', check);
    check();
  });
};
